import { ReactNode, type FunctionComponent } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';

type InformationBoxProps = {
  /**
   * Optional title, larger and bolder than the main content
   */
  title?: string;
  /**
   * Main content
   */
  infoText: string | ReactNode | undefined;
  /**
   * Change the background colour and icon
   */
  variant?: 'warning' | 'info';
};

const InformationBoxStyles = cva(
  'max-w-form relative flex w-full items-start gap-4 rounded bg-opacity-40 p-5',
  {
    variants: {
      variant: {
        warning: 'bg-status-warning',
        info: 'bg-status-info',
      },
    },
    defaultVariants: {
      variant: 'warning',
    },
  },
);

export const InformationBox: FunctionComponent<InformationBoxProps> = ({
  title,
  infoText,
  variant,
}) => {
  const classes = clsx(InformationBoxStyles({ variant }));

  const SVGPath = () => {
    if (variant === 'info') {
      return 'M9 0V8.71429H0V0H9ZM0.473709 18.1429H8.52631V53.5714V60H0.473684L0.473709 18.1429Z';
    } else {
      return 'M9 0L8.31818 42.6761H0.75L0 0H9ZM8.72694 50.0283V60.0002H0.340576V50.0283H8.72694Z';
    }
  };

  const renderInfoText = () => {
    if (typeof infoText === 'string') {
      return (
        <span
          className="text-body break-words"
          dangerouslySetInnerHTML={{ __html: infoText?.toString() }}
        />
      );
    } else {
      return <span className="text-body break-words">{infoText}</span>;
    }
  };

  return (
    <div className={classes}>
      <div>
        <svg
          width="9"
          height="60"
          viewBox="0 0 9 60"
          fill="fill-text-dark"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            id="icon"
            fillRule="evenodd"
            clipRule="evenodd"
            opacity="0.3"
            d={SVGPath()}
          />
        </svg>
      </div>
      <div className="text-text-dark">
        {title?.length !== undefined && title?.length > 0 && (
          <span className="block text-bodyXl mb-2 font-semibold">{title}</span>
        )}
        {renderInfoText()}
      </div>
    </div>
  );
};
